import { Component, OnInit, inject } from '@angular/core';
import { ClientsService } from '../../../core/services/clients.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { Client } from '../../../core/models/client.model';
import { ClientStatus } from '../../../core/enums/client-status.enum';
import { UserRoleName } from '../../../core/enums/user-role-name.enum';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ClientSftpCredentialService } from '../../../core/services/client-sftp-credentials.service';
import { TitleCasePipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ClientActionsComponent } from '../../components/client-actions/client-actions.component';
import { ClientStatsComponent } from '../../components/client-stats/client-stats.component';
import { BlankValueDashComponent } from '../../../shared/components/blank-value-dash/blank-value-dash.component';
import { PhoneNumberComponent } from '../../../shared/components/phone-number/phone-number.component';
import { SendDirectTestMessageComponent } from '../../components/send-direct-test-message/send-direct-test-message.component';
import { CopyToClipboardComponent } from '../../../shared/components/copy-to-clipboard/copy-to-clipboard.component';
import { SensitiveStringComponent } from '../../../shared/components/sensitive-string/sensitive-string.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { JoinListPipe } from '../../../shared/pipes/join-list.pipe';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';

const ehrInstanceValueBlankPlaceholder = "(blank)";

@Component({
    selector: 'app-capture-admin-client',
    templateUrl: './capture-admin-client.component.html',
    styleUrls: ['./capture-admin-client.component.scss'],
    imports: [ClientActionsComponent,
              ClientStatsComponent,
              BlankValueDashComponent,
              RouterLink,
              PhoneNumberComponent,
              SendDirectTestMessageComponent,
              CopyToClipboardComponent,
              SensitiveStringComponent,
              MatProgressSpinner,
              TitleCasePipe,
              CurrencyPipe,
              DatePipe,
              JoinListPipe,
              BlankValueDashPipe]
})
export class CaptureAdminClientComponent implements OnInit {
  private clientsService = inject(ClientsService);
  private authService = inject(AuthenticationService);
  private route = inject(ActivatedRoute);
  private clientSftpCredentialService = inject(ClientSftpCredentialService);

  client: Client;
  canManageClients = false;
  effectiveAllowedClientFileEhrInstanceValues: string[];
  loading = false;
  clientSftpCredentials = [];

  ngOnInit() {
    this.canManageClients = this.authService.hasPermissionTo(UserRoleName.manageClients);
    this.init();
  }

  private init() {
    const clientId = parseInt(this.route.snapshot.paramMap.get('id'), 10);

    this.loading = true;
    this.clientsService.get(clientId).subscribe(client => {
      this.client = client;

      this.effectiveAllowedClientFileEhrInstanceValues =
        client.effectiveAllowedClientFileEhrInstanceValues.map(value => value || ehrInstanceValueBlankPlaceholder);

      this.loadClientSftpCredentials();

      this.loading = false;
    });
  }

  onClientChange(client: Client) {
    this.client = client;
  }

  onPasswordAccessed(id: string) {
    this.loadClientSftpCredentialPassword(id);
  }

  get canManageImplementation() {
    return this.authService.hasPermissionTo(UserRoleName.manageImplementation);
  }

  get emrNames() {
    return this.client?.emrSystems.map(emr => emr.name).join(', ')
  }

  get deactivated() {
    return this.client.status === ClientStatus.deactivated
  }

  public assignedUsers() {
    const userNames = this.client.assignedUsers.map(u => u.email).sort();
    return userNames.join(', ');
  }

  public liveThirdPartyAdministers() {
    const names = this.client.liveThirdPartyAdministers.map(a => a.name).sort();
    return names.join(', ');
  }

  public entityTypes() {
    const types = this.client.entityTypes.sort();
    return types.join(', ');
  }

  public contractedThirdPartyAdministers() {
    const names = this.client.contractedThirdPartyAdministers.map(a => a.name).sort();
    return names.join(', ');
  }

  public applicableToClaimsTypes() {
    const types = [];

    if (this.client.applicableToReferralClaims) {
      types.push('Referral');
    }

    if (this.client.applicableToIccClaims) {
      types.push('Icc');
    }

    return types;
  }

  private loadClientSftpCredentials() {
    if (this.canManageImplementation) {
      this.clientSftpCredentialService.getCredentials(this.client.id).subscribe(result => {
        this.clientSftpCredentials = result;
      });
    }
  }

  private loadClientSftpCredentialPassword(id: string) {
    if (this.canManageImplementation) {
      const credential = this.clientSftpCredentials.find(cred => cred.id === id);

      this.clientSftpCredentialService.getPassword(id).subscribe(result => {
        if (credential) credential.decryptedPassword = result.decryptedPassword;
      });
    }
  }
}
